import React from 'react';
import { useTranslation } from 'react-i18next';

export const FaqSection: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isSpanish = i18n.language === 'es';
    const [openIndex, setOpenIndex] = React.useState<number | null>(null);

    React.useEffect(() => {
        // Load weatherwidget.io script
        const scriptId = 'weatherwidget-io-js';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://weatherwidget.io/js/widget.min.js';
            document.body.appendChild(script);
        } else if ((window as any).__weatherwidget_init) {
            // Re-initialize if the script is already loaded
            (window as any).__weatherwidget_init();
        }
    }, []);

    const toggleAccordion = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const faqs = [
        {
            question: t('faq_section.q1'),
            answer: (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center flex flex-col items-center">
                            <h4 className="text-base md:text-base font-mono text-[#3C2312] mb-1">{t('faq_section.a1_subtitle1')}</h4>
                            <p className="text-[#3C2312] font-mono text-sm md:text-base tracking-wider">{t('faq_section.a1_text1_1')}<br />{t('faq_section.a1_text1_2')}</p>
                        </div>
                        <div className="text-center flex flex-col items-center">
                            <h4 className="text-base md:text-base font-mono text-[#3C2312] mb-1">{t('faq_section.a1_subtitle2')}</h4>
                            <p className="text-[#3C2312] font-mono text-sm md:text-base tracking-wider">{t('faq_section.a1_text2_1')}<br />{t('faq_section.a1_text2_2')}</p>
                        </div>
                        <div className="text-center flex flex-col items-center">
                            <h4 className="text-base md:text-base font-mono text-[#3C2312] mb-1">{t('faq_section.a1_subtitle3')}</h4>
                            <p className="text-[#3C2312] font-mono text-sm md:text-base tracking-wider">{t('faq_section.a1_text3_1')}<br />{t('faq_section.a1_text3_2')}</p>
                        </div>
                    </div>

                    <div className="w-full overflow-hidden rounded-lg mt-6">
                        <h4 className="text-sm md:text-base font-mono text-[#3C2312] mb-4">{t('faq_section.a1_forecast')}</h4>
                        <div className="w-full min-h-[150px]">
                            <a
                                className="weatherwidget-io"
                                href="https://forecast7.com/en/38d99n1d86/albacete/"
                                data-label_1="ALBACETE"
                                data-label_2="WEATHER"
                                data-font="Courier New"
                                data-icons="Climacons Animated"
                                data-theme="original"
                                data-basecolor="#A8ADA9"
                                data-accent="rgba(60, 35, 18, 0.05)"
                                data-textcolor="#3C2312"
                                data-highcolor="#3C2312"
                                data-lowcolor="#3C2312"
                                data-suncolor="#D4AF37"
                                data-mooncolor="#3C2312"
                                data-cloudcolor="#3C2312"
                                data-cloudfill="rgba(255, 255, 255, 0.1)"
                                data-raincolor="#3C2312"
                                data-snowcolor="#3C2312"
                            >
                                {t('faq_section.a1_weather_btn')}
                            </a>
                        </div>
                    </div>
                </div>
            )
        },
        {
            question: t('faq_section.q2'),
            answer: (
                <p className="text-[#3C2312] font-mono text-sm md:text-base tracking-wider leading-relaxed">
                    {t('faq_section.a2')}
                </p>
            )
        },
        {
            question: t('faq_section.q3'),
            answer: (
                <div className="space-y-4 text-[#3C2312] font-mono text-sm md:text-base tracking-wider leading-relaxed">
                    <p>
                        {t('faq_section.a3_p1')}
                    </p>
                    <p>
                        <span className="underline">{t('faq_section.a3_ladies')}</span>{t('faq_section.a3_ladies_text')}
                    </p>
                    <p>
                        <span className="underline">{t('faq_section.a3_gentlemen')}</span>{t('faq_section.a3_gentlemen_text')}
                    </p>
                    <p>
                        <span className="underline">{t('faq_section.a3_evening')}</span>{t('faq_section.a3_evening_text')}
                    </p>
                    <p className="mt-6">
                        {t('faq_section.a3_p5')}
                    </p>
                </div>
            )
        }
    ];

    return (
        <div id="faq" className="bg-[#A8ADA9] pt-12 pb-24 md:pt-16 md:pb-32 scroll-mt-20">
            <div className="shell max-w-4xl mx-auto px-4">
                <div className="flex flex-col items-center mb-6 md:mb-10 px-4">
                    <img
                        src={isSpanish ? "/images/text/preguntas_frecuentes.svg" : "/images/text/FAQ.svg"}
                        alt={t('nav.faq', 'Frequently Asked Questions')}
                        className="w-full max-w-[650px]"
                    />
                </div>

                <div className="w-full">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border-b border-white border-opacity-30">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full py-3 flex justify-between items-center text-left focus:outline-none"
                            >
                                <span className="font-display text-white text-base md:text-base tracking-widest uppercase pr-8">
                                    {faq.question}
                                </span>
                                <svg
                                    className={`w-6 h-6 text-white transform transition-transform duration-300 ease-in-out flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <div
                                className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-[1000px] opacity-100 mb-4' : 'max-h-0 opacity-0'
                                    }`}
                            >
                                <div className="pt-2">
                                    {faq.answer}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
